import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

// Determine how many page‑buttons to show based on viewport width
export const autoPerPage = (desktop, tablet, mobile) => {
  if (typeof window === 'undefined') return desktop;
  const w = window.innerWidth;
  if (w < 600) return mobile;
  if (w < 992) return tablet;
  return desktop;
};

const Pagination = ({
  page,
  pages,
  onPage,
  desktopButtons = 7,
  tabletButtons = 5,
  mobileButtons = 3,
}) => {
  // 1) ALWAYS call hooks up front:
  const maxButtons = autoPerPage(desktopButtons, tabletButtons, mobileButtons);
  const pageItems = useMemo(() => {
    // inside the memo you can guard pages <= 1
    if (pages <= 1) return [];

    // if it all fits, just show [1…pages]
    if (pages <= maxButtons) {
      return Array.from({ length: pages }, (_, i) => i + 1);
    }

    // otherwise slide a window of size maxButtons
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, page - half);
    let end = start + maxButtons - 1;

    if (end > pages) {
      end = pages;
      start = pages - maxButtons + 1;
    }

    const items = [];
    if (start > 1) {
      items.push(1);
      if (start > 2) items.push('ellipsis-start');
    }

    for (let i = start; i <= end; i++) {
      items.push(i);
    }

    if (end < pages) {
      if (end < pages - 1) items.push('ellipsis-end');
      items.push(pages);
    }

    return items;
  }, [page, pages, maxButtons]);

  // 2) Now it’s safe to bail out:
  if (pages <= 1) return null;

  return (
    <nav className="rr-pagination" aria-label="Pagination">
      <button
        type="button"
        disabled={page === 1}
        onClick={() => onPage(page - 1)}
      >
        Prev
      </button>

      {pageItems.map((item, idx) =>
        typeof item === 'number' ? (
          <button
            key={item}
            type="button"
            className={item === page ? 'active' : ''}
            onClick={() => onPage(item)}
          >
            {item}
          </button>
        ) : (
          <span key={item + idx} className="ellipsis">
            &hellip;
          </span>
        )
      )}

      <button
        type="button"
        disabled={page === pages}
        onClick={() => onPage(page + 1)}
      >
        Next
      </button>
    </nav>
  );
};

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  pages: PropTypes.number.isRequired,
  onPage: PropTypes.func.isRequired,
  desktopButtons: PropTypes.number,
  tabletButtons: PropTypes.number,
  mobileButtons: PropTypes.number,
};

export default Pagination;
